import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowLeft, Buildings, GlobeHemisphereWest, Spinner } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import API from "../lib/api";
import NearbySuppliers from "../components/NearbySuppliers";
import AddressAutocomplete from "../components/AddressAutocomplete";

const TRADE_OPTIONS = [
  "All Trades", "Plumbing", "Electrical", "Gas Safe", "Roofing", "Carpentry",
  "Painting", "Flooring", "Heating / HVAC", "Glazing", "Locksmith", "Gardening",
  "Cleaning", "Pest Control", "General Maintenance", "Drainage", "Fire Safety",
];

export default function NearbySuppliersPage() {
  const [mode, setMode] = useState("project"); // project | address
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [addrLat, setAddrLat] = useState(null);
  const [addrLng, setAddrLng] = useState(null);
  const [addrLabel, setAddrLabel] = useState("");
  const [trade, setTrade] = useState("");
  const [radius, setRadius] = useState(50);
  const [geocoding, setGeocoding] = useState(false);

  const handleGeocodeAll = async () => {
    if (!window.confirm("This will find all suppliers missing map coordinates and geocode them from their address. Continue?")) return;
    setGeocoding(true);
    try {
      const { data } = await API.post("/suppliers-nearby/backfill-geocode");
      const { scanned = 0, updated = 0, failed = 0 } = data || {};
      if (scanned === 0) {
        toast.success("All suppliers are already geocoded");
      } else if (updated > 0) {
        toast.success(`Geocoded ${updated} supplier${updated === 1 ? "" : "s"}${failed ? ` — ${failed} failed (invalid address)` : ""}`);
      } else {
        toast.error(`Scanned ${scanned} suppliers — ${failed} could not be geocoded (invalid or placeholder address). Please edit their address.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Geocoding failed");
    } finally {
      setGeocoding(false);
    }
  };

  useEffect(() => {
    API.get("/projects?limit=500").then(({ data }) => {
      setProjects(data.projects || data || []);
    }).catch(() => {});
  }, []);

  const origin = mode === "project"
    ? { projectId }
    : { latitude: addrLat, longitude: addrLng };

  const hasOrigin = mode === "project" ? !!projectId : (addrLat && addrLng);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="nearby-suppliers-page">
      <div className="flex items-center gap-3">
        <Link to="/vendors">
          <Button variant="ghost" size="icon" data-testid="back-to-suppliers">
            <ArrowLeft size={16} />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-semibold font-[Chivo] flex items-center gap-2">
            <MapPin size={24} weight="duotone" className="text-primary" />
            Nearby Suppliers
          </h1>
          <p className="text-xs text-muted-foreground">Find the closest suppliers to a project, property, or any UK address.</p>
        </div>
        <div className="ml-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={handleGeocodeAll}
            disabled={geocoding}
            data-testid="geocode-all-btn"
            title="Find coordinates for suppliers missing location data"
          >
            {geocoding
              ? <Spinner size={14} className="animate-spin mr-1.5" />
              : <GlobeHemisphereWest size={14} weight="duotone" className="mr-1.5" />}
            {geocoding ? "Geocoding..." : "Geocode all suppliers"}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-border rounded-md p-4 bg-card space-y-4">
        <div className="flex gap-2">
          <Button
            size="sm"
            variant={mode === "project" ? "default" : "outline"}
            onClick={() => setMode("project")}
            data-testid="mode-project"
          >
            <Buildings size={14} className="mr-1" /> By Project
          </Button>
          <Button
            size="sm"
            variant={mode === "address" ? "default" : "outline"}
            onClick={() => setMode("address")}
            data-testid="mode-address"
          >
            <MapPin size={14} className="mr-1" /> By Address
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {mode === "project" ? (
            <div className="sm:col-span-3">
              <Label className="text-xs">Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger className="mt-1" data-testid="nearby-project-select">
                  <SelectValue placeholder="Select project..." />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name} {p.property_address ? `— ${p.property_address.slice(0, 60)}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="sm:col-span-3">
              <Label className="text-xs">Search any UK address</Label>
              <AddressAutocomplete
                value={addrLabel}
                onChange={setAddrLabel}
                onSelect={(a) => {
                  setAddrLat(a.latitude);
                  setAddrLng(a.longitude);
                  setAddrLabel(a.formatted_address || "");
                }}
                placeholder="42 High Street, London..."
                data-testid="nearby-address-input"
              />
            </div>
          )}

          <div>
            <Label className="text-xs">Trade filter</Label>
            <Select value={trade || "all"} onValueChange={v => setTrade(v === "all" ? "" : v)}>
              <SelectTrigger className="mt-1" data-testid="nearby-trade-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All trades</SelectItem>
                {TRADE_OPTIONS.slice(1).map(t => <SelectItem key={t} value={t.toLowerCase()}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Custom trade (free text)</Label>
            <Input
              value={trade}
              onChange={e => setTrade(e.target.value)}
              placeholder="e.g. asbestos"
              className="mt-1"
              data-testid="nearby-trade-custom"
            />
          </div>

          <div>
            <Label className="text-xs">Radius</Label>
            <Select value={String(radius)} onValueChange={v => setRadius(Number(v))}>
              <SelectTrigger className="mt-1" data-testid="nearby-radius-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10 miles</SelectItem>
                <SelectItem value="25">25 miles</SelectItem>
                <SelectItem value="50">50 miles</SelectItem>
                <SelectItem value="100">100 miles</SelectItem>
                <SelectItem value="250">250 miles</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Results */}
      {!hasOrigin && (
        <div className="py-12 text-center text-muted-foreground text-sm border border-dashed border-border rounded-md">
          Pick a project or enter an address to find nearby suppliers.
        </div>
      )}

      {hasOrigin && (
        <NearbySuppliers
          projectId={origin.projectId || ""}
          latitude={origin.latitude || null}
          longitude={origin.longitude || null}
          trade={trade}
          radiusMiles={radius}
          limit={50}
          title={mode === "project" ? "Suppliers near this project" : `Suppliers near "${addrLabel}"`}
          includeCompliance
          data-testid="nearby-results"
        />
      )}
    </div>
  );
}
